
// Color palette for the portfolio website
type ThemeMode = 'light-mode' | 'dark-mode';

class ColorPalette {
  // Theme configuration
  private pageColor: ThemeMode = 'light-mode';
  
  // Light mode color definitions
  private lightModeColors = {
    surfacePrimary: '#ffffff',        // (white) Used for: App background, overlay backgrounds, section titles background, button text
    textPrimary: '#000000',           // (black) Used for: Desktop navbar background, borders, text content, timeline elements, table borders
    navbarBackground: '#111111',      // (darkGray) Used for: Base navbar background (desktop sidebar)
    navbarHover: '#575757',           // (mediumGray) Used for: Hover states on navigation links, close button background, overlay button states
    buttonHover: '#4a4a4a',           // (lightGray) Used for: Menu button hover states, close button hover background
    timelineBar: '#333333',           // (timelineGray) Used for: Career timeline vertical bar (desktop and mobile views)
    textSecondary: '#666666',         // (textGray) Used for: Secondary text (dates, locations, code comments, loading messages)
    interactiveHover: '#313131',      // (hoverGray) Used for: Skill item hover effects in Home component
    surfaceSecondary: 'rgba(128, 128, 128, 0.25)', // (backgroundGray) Used for: GitHub topic tags, table headers, code blocks, subtle backgrounds
    cardSurface: 'rgba(255, 255, 255, 0.05)', // (cardBackground) Used for: Project card backgrounds
    accentPrimary: '#646464ff', //'#64ffda',         // (linkCyan) Used for: GitHub links, accent borders, focus outlines, repository links, blockquote borders
    accentHover: '#646464ff', //'#9effeb',           // (linkCyanHover) Used for: Link hover states, view project button hover
    surfaceTransparent: 'rgba(255, 255, 255, 0)',     // (whiteTransparent) Used for: Overlay states, transparent backgrounds
    surfaceSemiTransparent: 'rgba(255, 255, 255, 0.75)', // (whiteSemiTransparent) Note: Defined but not actively used in current CSS
    surfaceAlmostOpaque: 'rgba(255, 255, 255, 0.99)',     // (whiteAlmostOpaque) Note: Defined but not actively used in current CSS  
    surfaceOpaque: 'rgba(255, 255, 255, 1)',             // (whiteOpaque) Note: Defined but not actively used in current CSS
    navbarGradientBackground: 'linear-gradient(to top, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.75), rgba(255, 255, 255, 0.99), rgba(255, 255, 255, 0.99), rgba(255, 255, 255, 1), rgba(255, 255, 255, 1), rgba(255, 255, 255, 1), rgba(255, 255, 255, 1))', // (navbarGradient) Used for: Base navbar gradient background
    mobileNavGradientBackground: 'linear-gradient(to top, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.90), rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 1), rgba(255, 255, 255, 1), rgba(255, 255, 255, 1), rgba(255, 255, 255, 1))' // (mobileNavGradient) Used for: Mobile navigation dropdown background
  };
  
  // Dark mode color definitions - complete dark theme palette
  private darkModeColors = {
    surfacePrimary: '#0f0f0f',        // (darkBackground) Used for: App background, overlay backgrounds, section titles background
    textPrimary: '#f5f5f5',           // (lightText) Used for: Primary text content, borders, timeline elements
    navbarBackground: '#1a1a1a',      // (darkNavbar) Used for: Base navbar background (desktop sidebar)
    navbarHover: '#b8b8b8ff',           // (darkNavbarHover) Used for: Hover states on navigation links, interactive elements
    buttonHover: '#404040',           // (darkButtonHover) Used for: Menu button hover states, interactive button backgrounds
    timelineBar: '#555555',           // (darkTimelineBar) Used for: Career timeline vertical bar (desktop and mobile views)
    textSecondary: '#c0c0c0',         // (darkTextSecondary) Used for: Secondary text (dates, locations, code comments, loading messages)
    interactiveHover: '#2a2a2a',      // (darkInteractiveHover) Used for: Skill item hover effects in Home component
    surfaceSecondary: 'rgba(255, 255, 255, 0.1)', // (darkSurfaceSecondary) Used for: GitHub topic tags, table headers, code blocks, subtle backgrounds
    cardSurface: 'rgba(255, 255, 255, 0.05)', // (darkCardSurface) Used for: Project card backgrounds
    accentPrimary: '#646464ff', //'#8B5CF6',         // (claudePurple) Used for: GitHub links, accent borders, focus outlines, repository links, blockquote borders
    accentHover: '#646464ff', //'#7C3AED',           // (claudePurpleDeep) Used for: Link hover states, view project button hover
    surfaceTransparent: 'rgba(15, 15, 15, 0)',        // (darkTransparent) Used for: Overlay states, transparent backgrounds
    surfaceSemiTransparent: 'rgba(15, 15, 15, 0.75)', // (darkSemiTransparent) Note: Defined but not actively used in current CSS
    surfaceAlmostOpaque: 'rgba(15, 15, 15, 0.99)',    // (darkAlmostOpaque) Note: Defined but not actively used in current CSS  
    surfaceOpaque: 'rgba(15, 15, 15, 1)',             // (darkOpaque) Note: Defined but not actively used in current CSS
    navbarGradientBackground: 'linear-gradient(to top, rgba(15, 15, 15, 0), rgba(15, 15, 15, 0.75), rgba(15, 15, 15, 0.99), rgba(15, 15, 15, 0.99), rgba(15, 15, 15, 1), rgba(15, 15, 15, 1), rgba(15, 15, 15, 1), rgba(15, 15, 15, 1))', // (darkNavbarGradient) Used for: Base navbar gradient background
    mobileNavGradientBackground: 'linear-gradient(to top, rgba(15, 15, 15, 0), rgba(15, 15, 15, 0.90), rgba(15, 15, 15, 0.95), rgba(15, 15, 15, 0.98), rgba(15, 15, 15, 1), rgba(15, 15, 15, 1), rgba(15, 15, 15, 1), rgba(15, 15, 15, 1))' // (darkMobileNavGradient) Used for: Mobile navigation dropdown background
  };

  // Dynamic color getters based on current theme
  public get surfacePrimary(): string { return this.getCurrentThemeColors().surfacePrimary; }
  public get textPrimary(): string { return this.getCurrentThemeColors().textPrimary; }
  public get navbarBackground(): string { return this.getCurrentThemeColors().navbarBackground; }
  public get navbarHover(): string { return this.getCurrentThemeColors().navbarHover; }
  public get buttonHover(): string { return this.getCurrentThemeColors().buttonHover; }
  public get timelineBar(): string { return this.getCurrentThemeColors().timelineBar; }
  public get textSecondary(): string { return this.getCurrentThemeColors().textSecondary; }
  public get interactiveHover(): string { return this.getCurrentThemeColors().interactiveHover; }
  public get surfaceSecondary(): string { return this.getCurrentThemeColors().surfaceSecondary; }
  public get cardSurface(): string { return this.getCurrentThemeColors().cardSurface; }
  public get accentPrimary(): string { return this.getCurrentThemeColors().accentPrimary; }
  public get accentHover(): string { return this.getCurrentThemeColors().accentHover; }
  public get surfaceTransparent(): string { return this.getCurrentThemeColors().surfaceTransparent; }
  public get surfaceSemiTransparent(): string { return this.getCurrentThemeColors().surfaceSemiTransparent; }
  public get surfaceAlmostOpaque(): string { return this.getCurrentThemeColors().surfaceAlmostOpaque; }
  public get surfaceOpaque(): string { return this.getCurrentThemeColors().surfaceOpaque; }
  public get navbarGradientBackground(): string { return this.getCurrentThemeColors().navbarGradientBackground; }
  public get mobileNavGradientBackground(): string { return this.getCurrentThemeColors().mobileNavGradientBackground; }

  /**
   * Get the current theme colors based on pageColor setting
   */
  private getCurrentThemeColors() {
    return this.pageColor === 'light-mode' ? this.lightModeColors : this.darkModeColors;
  }

  /**
   * Get the current theme mode
   */
  public getTheme(): ThemeMode {
    return this.pageColor;
  }

  /**
   * Set the theme mode and update CSS custom properties
   */
  public setTheme(theme: ThemeMode): void {
    console.log('🔧 ColorPalette.setTheme called with:', theme);
    this.pageColor = theme;
    console.log('🔧 Theme set, current colors:', this.getCurrentThemeColors());
  }

  // Backward compatibility aliases (for any direct color access)
  public get white(): string { return this.surfacePrimary; }
  public get black(): string { return this.textPrimary; }
  public get darkGray(): string { return this.navbarBackground; }
  public get mediumGray(): string { return this.navbarHover; }
  public get lightGray(): string { return this.buttonHover; }
  public get timelineGray(): string { return this.timelineBar; }
  public get textGray(): string { return this.textSecondary; }
  public get hoverGray(): string { return this.interactiveHover; }
  public get backgroundGray(): string { return this.surfaceSecondary; }
  public get cardBackground(): string { return this.cardSurface; }
  public get linkCyan(): string { return this.accentPrimary; }
  public get linkCyanHover(): string { return this.accentHover; }
  public get whiteTransparent(): string { return this.surfaceTransparent; }
  public get whiteSemiTransparent(): string { return this.surfaceSemiTransparent; }
  public get whiteAlmostOpaque(): string { return this.surfaceAlmostOpaque; }
  public get whiteOpaque(): string { return this.surfaceOpaque; }
  public get navbarGradient(): string { return this.navbarGradientBackground; }
  public get mobileNavGradient(): string { return this.mobileNavGradientBackground; }

  /**
   * Get a color by name
   * @param colorName - The name of the color to retrieve
   * @returns The color value as a string
   */
  getColor(colorName: keyof ColorPalette): string {
    return this[colorName] as string;
  }

  /**
   * Get all available color names
   * @returns Array of color property names
   */
  getColorNames(): string[] {
    // Get only the theme color properties, not the backward compatibility aliases
    const themeColorKeys = Object.keys(this.lightModeColors);
    console.log('🏷️ Theme color keys:', themeColorKeys);
    return themeColorKeys;
  }

  /**
   * Create a custom rgba color with specified opacity
   * @param baseColor - Base hex color (e.g., '#ffffff')
   * @param opacity - Opacity value between 0 and 1
   * @returns RGBA color string
   */
  createRgbaColor(baseColor: string, opacity: number): string {
    // Remove # if present
    const hex = baseColor.replace('#', '');
    
    // Parse hex to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  /**
   * Get CSS custom properties object for all colors
   * @returns Object with CSS custom property names and values
   */
  getCSSCustomProperties(): Record<string, string> {
    const properties: Record<string, string> = {};
    const currentColors = this.getCurrentThemeColors();
    
    Object.entries(currentColors).forEach(([colorName, colorValue]) => {
      // Convert camelCase to kebab-case for CSS custom properties
      const cssPropertyName = `--color-${colorName.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      properties[cssPropertyName] = colorValue;
      console.log(`🏷️ CSS Property: ${cssPropertyName} = ${colorValue}`);
    });
    
    return properties;
  }

  /**
   * Inject colors as CSS custom properties into the document root
   */
  injectCSSCustomProperties(): void {
    console.log('💉 Injecting CSS custom properties...');
    const root = document.documentElement;
    const properties = this.getCSSCustomProperties();
    
    console.log('💉 Properties to inject:', properties);
    
    Object.entries(properties).forEach(([property, value]) => {
      root.style.setProperty(property, value);
      console.log(`💉 Set ${property}: ${value}`);
    });
    
    console.log('💉 CSS injection complete');
  }
}

// Export singleton instance
export const colors = new ColorPalette();

// Export types for TypeScript support
export type ColorKey = keyof ColorPalette;
export type { ThemeMode };